import { fetchFlask } from '../../utils/fetchFlask';
import {
    formatIsoDate,
    getDayCountInRange,
    normalizeDayStart
} from '../../utils/dateUtils';

export const CONVERSAO_LPS_API = 'crm/getConversaoLPs';
export const MAX_COMPARED_LPS = 4;
export const MIN_RELEVANT_VISITS = 30;

export const CHART_METRICS = [
    { value: 'visitas', label: 'Visitas' },
    { value: 'leads', label: 'Leads' },
    { value: 'conversao', label: 'Conversão acumulada' }
];

const METRIC_FIELDS = ['visitas', 'leads', 'leads_fechados'];

const getResponseRows = (response, api) => {
    if (!Array.isArray(response?.data)) {
        throw new Error(response?.message || `Resposta inválida de ${api}.`);
    }

    return response.data;
};

export const buildConversaoLPsPayload = ({ startDate, endDate }) => JSON.stringify({
    data_inicio: formatIsoDate(startDate),
    data_fim: formatIsoDate(endDate)
});

const toMetricRow = (row) => ({
    lpId: String(row.lp_id),
    nome: row.nome,
    url: row.url,
    data: row.data,
    visitas: Number(row.visitas),
    leads: Number(row.leads),
    leads_fechados: Number(row.leads_fechados)
});

const fetchConversaoLPs = async (dateRange) => {
    const response = await fetchFlask(CONVERSAO_LPS_API, buildConversaoLPsPayload(dateRange));
    return getResponseRows(response, CONVERSAO_LPS_API).map(toMetricRow);
};

const shiftDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

export const buildPreviousDateRange = ({ startDate, endDate }) => {
    const start = normalizeDayStart(startDate);
    const dayCount = getDayCountInRange(startDate, endDate);

    return {
        startDate: shiftDays(start, -dayCount),
        endDate: shiftDays(start, -1)
    };
};

export const fetchConversaoLPsPeriods = async (dateRange) => {
    const [rows, previousRows] = await Promise.all([
        fetchConversaoLPs(dateRange),
        fetchConversaoLPs(buildPreviousDateRange(dateRange))
    ]);

    return { rows, previousRows };
};

export const buildLandingPages = (rows) => {
    const landingPages = new Map();

    rows.forEach((row) => {
        if (!landingPages.has(row.lpId)) {
            landingPages.set(row.lpId, { id: row.lpId, nome: row.nome, url: row.url });
        }
    });

    return [...landingPages.values()].sort((left, right) => left.nome.localeCompare(right.nome, 'pt-BR'));
};

export const buildDayKeys = ({ startDate, endDate }) => {
    const start = normalizeDayStart(startDate);

    return Array.from(
        { length: getDayCountInRange(startDate, endDate) },
        (_, index) => formatIsoDate(shiftDays(start, index))
    );
};

const formatDayLabel = (isoDate) => {
    const [year, month, day] = isoDate.split('-');
    return { short: `${day}/${month}`, long: `${day}/${month}/${year}` };
};

export const getRate = (value, base) => (base > 0 ? (value / base) * 100 : null);

const emptyTotals = () => ({ visitas: 0, leads: 0, leads_fechados: 0 });

const addTotals = (totals, row) => {
    METRIC_FIELDS.forEach((field) => {
        totals[field] += row[field];
    });

    return totals;
};

const withRates = (totals) => ({
    ...totals,
    conversao: getRate(totals.leads, totals.visitas),
    fechamento: getRate(totals.leads_fechados, totals.leads)
});

export const getVisibleLandingPages = (landingPages, selectedIds) => {
    if (selectedIds.length === 0) return landingPages;

    const selectedIdSet = new Set(selectedIds);
    return landingPages.filter((landingPage) => selectedIdSet.has(landingPage.id));
};

export const buildLpStats = ({ landingPages, rows }) => {
    const totalsById = new Map(landingPages.map((landingPage) => [landingPage.id, emptyTotals()]));

    rows.forEach((row) => {
        const totals = totalsById.get(row.lpId);
        if (totals) addTotals(totals, row);
    });

    return landingPages.map((landingPage) => ({
        ...landingPage,
        ...withRates(totalsById.get(landingPage.id))
    }));
};

export const buildSummary = (stats) => withRates(stats.reduce(addTotals, emptyTotals()));

export const buildRowsSummary = (rows, selectedIds) => {
    const selectedIdSet = new Set(selectedIds);
    const selectedRows = selectedIds.length === 0 ? rows : rows.filter((row) => selectedIdSet.has(row.lpId));

    return buildSummary(selectedRows);
};

export const getCountDelta = (current, previous) => (
    previous > 0 ? ((current - previous) / previous) * 100 : null
);

export const getRateDelta = (current, previous) => (
    current !== null && previous !== null ? current - previous : null
);

export const buildDailyChartRows = ({ dayKeys, lpIds, rows }) => {
    const lpIdSet = new Set(lpIds);
    const totalsByDay = new Map();

    rows.forEach((row) => {
        if (!lpIdSet.has(row.lpId)) return;
        totalsByDay.set(row.data, addTotals(totalsByDay.get(row.data) ?? emptyTotals(), row));
    });

    const cumulative = emptyTotals();

    return dayKeys.map((data) => {
        const day = totalsByDay.get(data) ?? emptyTotals();
        addTotals(cumulative, day);
        const label = formatDayLabel(data);

        return {
            data,
            label: label.long,
            shortLabel: label.short,
            visitas: day.visitas,
            leads: day.leads,
            conversao: getRate(cumulative.leads, cumulative.visitas) ?? 0,
            hasVisits: cumulative.visitas > 0
        };
    });
};

export const getSeriesKey = (lpId, metric) => `lp_${lpId}_${metric}`;

export const buildCompareChartRows = ({ dayKeys, lpIds, rows }) => {
    const seriesByLp = lpIds.map((lpId) => ({
        lpId,
        dailyRows: buildDailyChartRows({ dayKeys, lpIds: [lpId], rows })
    }));

    return dayKeys.map((data, index) => {
        const label = formatDayLabel(data);

        return seriesByLp.reduce((chartRow, { lpId, dailyRows }) => {
            const dailyRow = dailyRows[index];

            return {
                ...chartRow,
                [getSeriesKey(lpId, 'visitas')]: dailyRow.visitas,
                [getSeriesKey(lpId, 'leads')]: dailyRow.leads,
                [getSeriesKey(lpId, 'conversao')]: dailyRow.conversao,
                [getSeriesKey(lpId, 'hasVisits')]: dailyRow.hasVisits
            };
        }, { data, label: label.long, shortLabel: label.short });
    });
};

const compareRates = (left, right) => {
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;

    return right - left;
};

export const RANKING_SORTS = {
    conversao: (left, right) => compareRates(left.conversao, right.conversao) || right.visitas - left.visitas,
    visitas: (left, right) => right.visitas - left.visitas,
    leads: (left, right) => right.leads - left.leads,
    fechamento: (left, right) => compareRates(left.fechamento, right.fechamento) || right.leads - left.leads
};

export const getHighlights = (stats) => {
    const eligible = stats.filter((stat) => stat.visitas >= MIN_RELEVANT_VISITS && stat.conversao !== null);

    if (eligible.length < 2) return { bestId: null, worstId: null };

    const sorted = [...eligible].sort(RANKING_SORTS.conversao);
    const best = sorted[0];
    const worst = sorted[sorted.length - 1];

    return best.conversao === worst.conversao
        ? { bestId: null, worstId: null }
        : { bestId: best.id, worstId: worst.id };
};

export const getDefaultCompareIds = (stats) => {
    const withTraffic = stats.filter((stat) => stat.visitas > 0).sort(RANKING_SORTS.visitas);
    const source = withTraffic.length >= 2 ? withTraffic : stats;

    return source.slice(0, 2).map((stat) => stat.id);
};

const getMedian = (values) => {
    const sorted = [...values].sort((left, right) => left - right);
    const middle = Math.floor(sorted.length / 2);

    return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

export const buildMatrixData = (stats) => {
    const points = stats.filter((stat) => stat.visitas >= MIN_RELEVANT_VISITS);

    if (points.length < 2) return null;

    return {
        points,
        medianVisitas: getMedian(points.map((point) => point.visitas)),
        medianConversao: getMedian(points.map((point) => point.conversao))
    };
};
